import React, { useState, useEffect } from 'react'
import {
  IoArrowBack,
  IoBookmarkOutline,
  IoBookmark,
  IoShareOutline,
  IoWarningOutline,
  IoFlameOutline,
  IoTrophyOutline,
  IoArrowDownOutline,
  IoAddOutline,
  IoCheckmarkCircle,
  IoCloseCircle,
} from 'react-icons/io5'
import { useCardsFixture, useCardsSavedFixtures, unsaveCardsFixture } from '../../swr-config'
import type { CardsFixtureDetailResponse } from '../../types'
import { RefereePanel } from './panels/RefereePanel'
import { TeamDisciplinePanel } from './panels/TeamDisciplinePanel'
import { PlayerCardsPanel } from './panels/PlayerCardsPanel'
import { MatchContextPanel } from './panels/MatchContextPanel'
import { ProjectionsPanel } from './panels/ProjectionsPanel'
import { NarrativeSummary } from './components/NarrativeSummary'
import { ConfidenceIndicator } from './components/ConfidenceIndicator'
import { SaveToWatchlistModal } from './components/SaveToWatchlistModal'
import { ErrorState } from '../ErrorState'
import { fmtKickoff } from '../../utils'
import toast from 'react-hot-toast'

interface CardsFixtureDetailProps {
  fixtureId: number
  onBack: () => void
}

type DetailTab = 'referee' | 'teams' | 'players' | 'context'

export function CardsFixtureDetail({ fixtureId, onBack }: CardsFixtureDetailProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('referee')
  const [isSaved, setIsSaved] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)

  const { data, error, isLoading, mutate } = useCardsFixture(fixtureId)
  const { data: savedData, mutate: mutateSaved } = useCardsSavedFixtures()

  // Check if this fixture is already saved
  useEffect(() => {
    if (savedData?.saved_fixtures) {
      const isFixtureSaved = savedData.saved_fixtures.some(
        (sf) => sf.fixture_id === fixtureId
      )
      setIsSaved(isFixtureSaved)
    }
  }, [savedData, fixtureId])

  const handleUnsave = async () => {
    if (isSaving) return

    setIsSaving(true)
    try {
      await unsaveCardsFixture(fixtureId)
      setIsSaved(false)
      mutateSaved()
      toast.success('Fixture removed from saved')
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove fixture')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveClick = () => {
    if (isSaved) {
      // If already saved, allow unsaving or show modal to add to watchlist
      handleUnsave()
    } else {
      // Show modal for saving with options
      setShowSaveModal(true)
    }
  }

  const handleSaved = () => {
    setIsSaved(true)
    mutateSaved()
  }

  const handleShare = () => {
    if (navigator.share && data) {
      navigator.share({
        title: `Cards Analysis: ${data.fixture.home_team_name} vs ${data.fixture.away_team_name}`,
        text: data.narrative,
        url: window.location.href,
      }).catch(() => {
        // User cancelled or sharing not supported
      })
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href)
      toast.success('Link copied to clipboard')
    }
  }

  if (isLoading) {
    return (
      <div className="cards-fixture-detail">
        <div className="cards-detail-header">
          <button type="button" className="cards-back-btn" onClick={onBack}>
            <IoArrowBack size={20} />
            <span>Back</span>
          </button>
        </div>
        <div className="cards-loading">
          <div className="loading-spinner" />
          <p>Loading card analysis...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="cards-fixture-detail">
        <div className="cards-detail-header">
          <button type="button" className="cards-back-btn" onClick={onBack}>
            <IoArrowBack size={20} />
            <span>Back</span>
          </button>
        </div>
        <ErrorState
          title="Failed to load analysis"
          message={error?.message || 'Fixture not found'}
          onRetry={() => mutate()}
        />
      </div>
    )
  }

  const { fixture, projections, confidence, context, referee } = data

  return (
    <div className="cards-fixture-detail">
      {/* Sticky Header */}
      <div className="cards-detail-header">
        <button type="button" className="cards-back-btn" onClick={onBack}>
          <IoArrowBack size={20} />
          <span>Back</span>
        </button>

        <div className="cards-detail-actions">
          <button
            type="button"
            className={`cards-action-btn ${isSaved ? 'active' : ''}`}
            onClick={handleSaveClick}
            disabled={isSaving}
            aria-label={isSaved ? 'Remove from saved' : 'Save fixture'}
            title={isSaved ? 'Remove from saved' : 'Save fixture'}
          >
            {isSaved ? <IoBookmark size={18} /> : <IoBookmarkOutline size={18} />}
          </button>
          {isSaved && (
            <button
              type="button"
              className="cards-action-btn"
              onClick={() => setShowSaveModal(true)}
              aria-label="Add to watchlist"
              title="Add to watchlist"
            >
              <IoAddOutline size={18} />
            </button>
          )}
          <button
            type="button"
            className="cards-action-btn"
            onClick={handleShare}
            aria-label="Share"
          >
            <IoShareOutline size={18} />
          </button>
        </div>
      </div>

      {/* Match Header */}
      <div className="cards-match-header">
        <div className="cards-match-teams">
          <div className="cards-match-team">
            {fixture.home_team_logo && (
              <img src={fixture.home_team_logo} alt="" className="cards-match-logo" />
            )}
            <span className="cards-match-team-name">{fixture.home_team_name}</span>
          </div>
          {fixture.status === 'FT' && fixture.home_score != null && fixture.away_score != null ? (
            <div className="cards-match-score">
              <span className="cards-match-score-value">{fixture.home_score} - {fixture.away_score}</span>
              <span className="cards-match-score-label">Full Time</span>
            </div>
          ) : (
            <span className="cards-match-vs">vs</span>
          )}
          <div className="cards-match-team">
            {fixture.away_team_logo && (
              <img src={fixture.away_team_logo} alt="" className="cards-match-logo" />
            )}
            <span className="cards-match-team-name">{fixture.away_team_name}</span>
          </div>
        </div>

        <div className="cards-match-info">
          <span>{fmtKickoff(fixture.kickoff)}</span>
          <span className="cards-match-info-sep">|</span>
          <span>{fixture.competition_name}</span>
          {fixture.venue && (
            <>
              <span className="cards-match-info-sep">|</span>
              <span>{fixture.venue}</span>
            </>
          )}
        </div>

        <div className="cards-match-referee">
          {referee ? (
            <span>
              Referee: <strong>{referee.name}</strong>
              {!referee.assigned && <span className="cards-tbd-badge">TBD</span>}
            </span>
          ) : (
            <span className="cards-match-referee-tbd">Referee not assigned</span>
          )}
        </div>

        {/* Risk Flags */}
        <div className="cards-risk-flags">
          {context.is_rivalry && (
            <span className="cards-risk-flag cards-risk-derby">
              <IoFlameOutline size={14} />
              {context.rivalry_name || 'Derby'}
            </span>
          )}
          {context.table_pressure?.home === 'title_race' || context.table_pressure?.away === 'title_race' ? (
            <span className="cards-risk-flag cards-risk-title">
              <IoTrophyOutline size={14} />
              Title Race
            </span>
          ) : null}
          {context.table_pressure?.home === 'relegation' || context.table_pressure?.away === 'relegation' ? (
            <span className="cards-risk-flag cards-risk-relegation">
              <IoArrowDownOutline size={14} />
              Relegation Battle
            </span>
          ) : null}
          {referee && referee.league_percentile && referee.league_percentile >= 75 && (
            <span className="cards-risk-flag cards-risk-strict-ref">
              <IoWarningOutline size={14} />
              High-card Ref
            </span>
          )}
        </div>

        {/* Main Projection Box */}
        <div className="cards-projection-box">
          <div className="cards-projection-main">
            <span className="cards-projection-value">{projections.total_yellow_cards.toFixed(1)}</span>
            <span className="cards-projection-label">Projected Cards</span>
          </div>
          <ConfidenceIndicator
            score={confidence.overall / 100}
            level={confidence.level}
            showLabel
          />
        </div>

        {/* Enhanced Confidence Section */}
        <div className="cards-confidence-section">
          <ConfidenceIndicator
            score={confidence.overall}
            level={confidence.level}
            showBar
            flags={confidence.flags}
            tooltipPosition="bottom"
          />
        </div>
      </div>

      {/* Narrative Summary */}
      <NarrativeSummary narrative={data.narrative} />

      {/* Tab Navigation */}
      <div className="cards-detail-tabs">
        <button
          type="button"
          className={`cards-tab ${activeTab === 'referee' ? 'active' : ''}`}
          onClick={() => setActiveTab('referee')}
        >
          Referee
        </button>
        <button
          type="button"
          className={`cards-tab ${activeTab === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveTab('teams')}
        >
          Teams
        </button>
        <button
          type="button"
          className={`cards-tab ${activeTab === 'players' ? 'active' : ''}`}
          onClick={() => setActiveTab('players')}
        >
          Players
        </button>
        <button
          type="button"
          className={`cards-tab ${activeTab === 'context' ? 'active' : ''}`}
          onClick={() => setActiveTab('context')}
        >
          Context
        </button>
      </div>

      {/* Tab Content */}
      <div className="cards-detail-content">
        {activeTab === 'referee' && <RefereePanel referee={data.referee} />}
        {activeTab === 'teams' && (
          <TeamDisciplinePanel
            homeTeam={data.home_team_discipline}
            awayTeam={data.away_team_discipline}
          />
        )}
        {activeTab === 'players' && <PlayerCardsPanel players={data.players} />}
        {activeTab === 'context' && <MatchContextPanel context={data.context} />}
      </div>

      {/* Projections Panel (Always Visible) */}
      <ProjectionsPanel
        projections={data.projections}
        explainability={data.explainability}
        explainabilityDetailed={data.explainability_detailed}
        scenarios={data.scenarios}
        odds={data.odds}
      />

      {/* How Did We Do? - Completed Fixtures */}
      {fixture.status === 'FT' && (() => {
        // Calculate actual cards from either data.actual_cards or fixture's card counts
        const actualCards = data.actual_cards ?? (
          (fixture.home_yellows != null || fixture.away_yellows != null)
            ? (fixture.home_yellows ?? 0) + (fixture.away_yellows ?? 0)
            : null
        )

        if (actualCards == null) return null

        // Calculate over/under result if not provided
        const overUnderResult = data.over_under_result ?? (() => {
          const line = 4.5 // Default line
          const projected = projections.total_yellow_cards
          if (projected > line) {
            return actualCards > line ? 'correct' : 'incorrect'
          } else if (projected < line) {
            return actualCards < line ? 'correct' : 'incorrect'
          }
          return null
        })()

        return (
          <div className="cards-how-did-we-do">
            <h3 className="cards-section-title">How Did We Do?</h3>

            {/* Final Cards Breakdown */}
            {(fixture.home_yellows != null || fixture.away_yellows != null) && (
              <div className="cards-final-breakdown">
                <div className="cards-final-team">
                  <span className="cards-final-team-name">{fixture.home_team_name}</span>
                  <span className="cards-final-yellows">
                    {fixture.home_yellows ?? 0} yellow{(fixture.home_yellows ?? 0) !== 1 ? 's' : ''}
                  </span>
                  {fixture.home_reds != null && fixture.home_reds > 0 && (
                    <span className="cards-final-reds">{fixture.home_reds} red</span>
                  )}
                </div>
                <div className="cards-final-team">
                  <span className="cards-final-team-name">{fixture.away_team_name}</span>
                  <span className="cards-final-yellows">
                    {fixture.away_yellows ?? 0} yellow{(fixture.away_yellows ?? 0) !== 1 ? 's' : ''}
                  </span>
                  {fixture.away_reds != null && fixture.away_reds > 0 && (
                    <span className="cards-final-reds">{fixture.away_reds} red</span>
                  )}
                </div>
              </div>
            )}

            <div className="cards-accuracy-comparison">
              <div className="cards-accuracy-item">
                <span className="cards-accuracy-label">Projected</span>
                <span className="cards-accuracy-value">{projections.total_yellow_cards.toFixed(1)}</span>
              </div>
              <div className="cards-accuracy-vs">vs</div>
              <div className="cards-accuracy-item">
                <span className="cards-accuracy-label">Actual</span>
                <span className="cards-accuracy-value">{actualCards}</span>
              </div>
              <div className="cards-accuracy-result">
                {Math.abs(actualCards - projections.total_yellow_cards) <= 1 ? (
                  <span className="cards-accuracy-badge accurate">
                    <IoCheckmarkCircle size={16} />
                    Accurate
                  </span>
                ) : (
                  <span className="cards-accuracy-badge inaccurate">
                    <IoCloseCircle size={16} />
                    Off by {Math.abs(actualCards - projections.total_yellow_cards).toFixed(1)}
                  </span>
                )}
              </div>
            </div>
            {overUnderResult && (
              <div className="cards-over-under-result">
                <span className={`cards-ou-badge ${overUnderResult === 'correct' ? 'correct' : 'incorrect'}`}>
                  {overUnderResult === 'correct' ? (
                    <>
                      <IoCheckmarkCircle size={14} />
                      Over/Under call correct
                    </>
                  ) : (
                    <>
                      <IoCloseCircle size={14} />
                      Over/Under call incorrect
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        )
      })()}

      {/* Save to Watchlist Modal */}
      {showSaveModal && (
        <SaveToWatchlistModal
          fixtureId={fixtureId}
          fixtureName={`${fixture.home_team_name} vs ${fixture.away_team_name}`}
          onClose={() => setShowSaveModal(false)}
          onSaved={handleSaved}
          isAlreadySaved={isSaved}
        />
      )}
    </div>
  )
}
